import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MARGIN = 8;
const MIN_HEIGHT = 180;

/**
 * A panel pinned under (or over) the control that opened it, rendered at the
 * top of the document.
 *
 * A grid scrolls, and a panel drawn inside a scrolling table is clipped by it
 * — that is what cut the column list and the unit editor in half. Portalling
 * it out of the table and positioning it from the trigger's on-screen box
 * fixes that once for every dropdown and editor.
 *
 * Dismissal lives here too: a click outside both the panel and its trigger, or
 * Escape, closes it. Scrolling or resizing re-pins it, so it never drifts away
 * from the cell it belongs to.
 *
 * @param {object} props
 * @param {{ current: HTMLElement|null }} props.anchorRef the control it belongs to
 * @param {() => void} props.onClose
 * @param {string} [props.className] classes for the panel itself
 * @param {"left"|"right"} [props.align="left"] which edge lines up with the anchor
 * @param {boolean} [props.matchWidth] at least as wide as the anchor
 * @param {React.ReactNode} props.children
 */
export default function AnchoredPanel({
  anchorRef,
  onClose,
  className = "",
  align = "left",
  matchWidth,
  children,
  ...rest
}) {
  const panelRef = useRef(null);
  const placedRef = useRef("");
  const [style, setStyle] = useState({ position: "fixed", top: 0, left: 0, visibility: "hidden" });

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const box = anchor.getBoundingClientRect();
    const below = window.innerHeight - box.bottom - MARGIN;
    const above = box.top - MARGIN;
    const flip = below < MIN_HEIGHT && above > below;

    const next = {
      position: "fixed",
      zIndex: 3000,
      maxHeight: Math.max(flip ? above : below, MIN_HEIGHT),
      ...(matchWidth ? { minWidth: box.width } : null),
    };
    if (flip) next.bottom = window.innerHeight - box.top + 4;
    else next.top = box.bottom + 4;

    const width = panel.getBoundingClientRect().width || box.width;
    const left = align === "right" ? box.right - width : box.left;
    next.left = Math.max(MARGIN, Math.min(left, window.innerWidth - width - MARGIN));

    const key = JSON.stringify(next);
    if (key === placedRef.current) return;
    placedRef.current = key;
    setStyle(next);
  }, [anchorRef, align, matchWidth]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (observer && panelRef.current) observer.observe(panelRef.current);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [place]);

  useEffect(() => {
    const onMouseDown = (e) => {
      const inPanel = panelRef.current?.contains(e.target);
      const inAnchor = anchorRef.current?.contains(e.target);
      if (!inPanel && !inAnchor) onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div ref={panelRef} className={className} style={style} {...rest}>
      {children}
    </div>,
    document.body,
  );
}
